import { GridColDef } from '@mui/x-data-grid-pro'
import { AxiosError } from 'axios'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import { spacing } from 'ext-ui/style/spacing'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  checkboxCellCss,
  mccGroupsBoxCss,
} from 'src/app/CustomFieldDetail/CustomFieldDetail.style'
import { selectCustomFieldIdFromRoute } from 'src/app/CustomFieldDetail/redux/customFieldDetail.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { BackendErrorToastMessage } from 'src/app/Toast/Toast'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LayoutTwoColumnPage from 'src/component-layout/LayoutTwoColumnPage/LayoutTwoColumnPage'
import Box from 'src/components/Box/Box'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import DataGrid, { DataGridHeaderStyle } from 'src/components/DataGrid/DataGrid'
import GenericLayoutDetailsComponent from 'src/components/GenericLayoutDetailsComponent/GenericLayoutDetailsComponent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import OnOffSwitchWithLabel from 'src/components/OnOffSwitchWithLabel/OnOffSwitchWithLabel'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { useQueryMccGroups } from 'src/queries/mccGroups.queries'
import {
  PatchAdditionalFieldRequest,
  usePatchAdditionalField,
  usePatchCustomFieldMccGroups,
  useQueryAdditionalFields,
} from 'src/queries/organizations/additionalFields.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectLoadedFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'

// OTHER is the catch-all bucket; a custom field with no mappings is already
// global, so exposing OTHER would be confusing.
const EXCLUDED_MCC_GROUP_IDS = new Set<string>(['OTHER'])

// MccGroupColumnField.Selected is also referenced in onCellClick to skip the
// checkbox cell (its Checkbox handles its own onChange).
const MccGroupColumnField = {
  Name: 'mccGroup',
  Selected: 'selected',
} as const

type MccRow = { id: string; mccGroup: string; selected: boolean }

const toTitleCase = (s: string): string =>
  s
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const CustomFieldDetail: React.FC = () => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const { analytics } = useAnalyticsContext()
  const appName = useSelector(selectAppName)
  const loadedRemoteConfig = useSelector(selectLoadedFromRemoteConfigState)
  const currentUser = useSelector(selectUserListModelFromState)
  const {
    enable_custom_fields_expense_fields: customFieldsExpenseFieldsEnabled,
  } = useSelector(selectFeatureFlaggerFromState) ?? {}
  const customFieldId = useSelector(selectCustomFieldIdFromRoute)
  const userCanViewPage = !!currentUser?.hasOrgBookkeeperPermissions
  const featureReady = !!customFieldsExpenseFieldsEnabled && userCanViewPage

  const { data: additionalFields, isLoading } = useQueryAdditionalFields({
    filters: {},
    enabled: featureReady,
  })
  const customField = additionalFields?.find(
    (field) => field.id === customFieldId,
  )
  const disableEditing = !customField?.editable

  const { data: mccGroups } = useQueryMccGroups({
    enabled: featureReady,
  })

  const { mutateAsync: patchField, isPending: patchFieldLoading } =
    usePatchAdditionalField()
  const { mutateAsync: patchMccGroups, isPending: patchMccGroupsPending } =
    usePatchCustomFieldMccGroups()

  const {
    fields,
    fieldsErrors,
    onFieldChange,
    setFieldsErrors,
    setErrorsAndReturnDidPassValidation,
  } = useFormFields<{ name: string }>({ name: '' }, () => ({
    name: [stringIsEmpty()],
  }))

  // Sync the form's name back to the server value when it changes (initial
  // load, refetch, or external update). onFieldChange is intentionally not in
  // deps: useFormFields recreates it every render and including it would loop.
  useEffect(() => {
    if (customField?.name !== undefined) {
      onFieldChange('name', customField.name)
    }
  }, [customField?.name])

  const showErrorToast = useCallback(
    (error: unknown): void => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <BackendErrorToastMessage
              error={error as AxiosError<BackendErrorPayload<string>>}
            />
          ),
          failureColorScheme: true,
          hideImage: true,
        }),
      )
    },
    [dispatch],
  )

  const selectedMccGroupIds = useMemo(
    () => new Set<string>(customField?.mccGroups ?? []),
    [customField?.mccGroups],
  )

  // Read the latest selected set + pending flag via refs inside
  // onToggleMccGroup so the handler stays stable across renders. If the
  // handler depended on either directly, the memoized column array would
  // rebuild on every toggle and MUI DataGrid would remount every cell
  // (flash). The pending ref also guards against rapid keyboard toggles
  // (Tab + Space), which the sx pointer-events block doesn't catch.
  const selectedMccGroupIdsRef = useRef(selectedMccGroupIds)
  selectedMccGroupIdsRef.current = selectedMccGroupIds
  const patchMccGroupsPendingRef = useRef(patchMccGroupsPending)
  patchMccGroupsPendingRef.current = patchMccGroupsPending

  const patchFieldSucceeded = useCallback(
    ({
      id,
      body,
    }: {
      id: string
      body: PatchAdditionalFieldRequest
    }): Promise<boolean> =>
      patchField({ id, body })
        .then((updated) => {
          analytics?.track('update custom field', {
            name: updated.name,
            required: updated.required,
            // PATCH /additionalfields doesn't enrich mccGroups, so fall back
            // to the cached selection — name/active/required toggles don't
            // change the MCC set.
            applied_merchant_categories:
              updated.mccGroups ?? Array.from(selectedMccGroupIdsRef.current),
          })
          return true
        })
        .catch((error: unknown) => {
          showErrorToast(error)
          return false
        }),
    [patchField, showErrorToast, analytics],
  )

  const onToggleMccGroup = useCallback(
    async (mccGroupId: string, nextChecked: boolean): Promise<void> => {
      if (patchMccGroupsPendingRef.current) return
      const next = new Set(selectedMccGroupIdsRef.current)
      if (nextChecked) next.add(mccGroupId)
      else next.delete(mccGroupId)
      const nextList = Array.from(next)
      try {
        const updated = await patchMccGroups({
          customFieldId,
          body: { mccGroups: nextList },
        })
        analytics?.track('update custom field', {
          name: updated.name,
          required: updated.required,
          applied_merchant_categories: updated.mccGroups ?? nextList,
        })
      } catch (error) {
        showErrorToast(error)
      }
    },
    [patchMccGroups, customFieldId, showErrorToast, analytics],
  )

  const mccGroupColumns = useMemo<GridColDef<MccRow>[]>(
    () => [
      {
        field: MccGroupColumnField.Name,
        headerName: translate('Merchant Category') as string,
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        flex: 1,
        minWidth: 200,
        renderCell: (params): React.ReactElement => (
          <DataGridCell params={params} text={params.row.mccGroup} />
        ),
      },
      {
        field: MccGroupColumnField.Selected,
        headerName: translate('Applies To') as string,
        disableColumnMenu: true,
        sortable: false,
        hideable: false,
        minWidth: 100,
        renderCell: (params): React.ReactElement => {
          const { id: groupId, mccGroup: groupName, selected } = params.row
          return (
            <DataGridCellWrapper params={params}>
              <div css={checkboxCellCss}>
                <Checkbox
                  checked={selected}
                  onChange={(e): void => {
                    void onToggleMccGroup(groupId, e.target.checked)
                  }}
                  aria-label={
                    translate('Select ${groupName} merchant category', {
                      groupName,
                    }) as string
                  }
                  disabled={disableEditing}
                  data-testid={`customFieldDetailMccGroupCheckbox-${groupId}`}
                />
              </div>
            </DataGridCellWrapper>
          )
        },
      },
    ],
    [translate, disableEditing, onToggleMccGroup],
  )

  const mccGroupRows = useMemo<MccRow[]>(
    () =>
      (mccGroups ?? [])
        .filter((group) => !EXCLUDED_MCC_GROUP_IDS.has(group.id))
        .map((group) => ({
          id: group.id,
          mccGroup: toTitleCase(group.group),
          selected: selectedMccGroupIds.has(group.id),
        })),
    [mccGroups, selectedMccGroupIds],
  )

  if (!loadedRemoteConfig) {
    return <LoadingBackgroundPage />
  }
  if (!featureReady) {
    return <NotFoundBackgroundPageContent />
  }
  if (isLoading) {
    return <LoadingBackgroundPage />
  }
  if (!customField) {
    return <NotFoundBackgroundPageContent />
  }

  return (
    <>
      <Helmet>
        <title>{`${customField.name} | ${appName}`}</title>
        <meta
          name="description"
          content={`${customField.name} ${translate('Custom Fields')} ${appName}`}
        />
      </Helmet>
      <LayoutPage padding={'medium'}>
        <LayoutTwoColumnPage
          detailsComponent={
            <GenericLayoutDetailsComponent
              staticContent={
                <div className={'flex-column gap-2'}>
                  <span className={'font-md text-secondary'}>
                    {translate('Field Name')}
                  </span>
                  <span
                    className={'font-h3'}
                    data-testid={'customFieldDetailName'}
                  >
                    {customField.name}
                  </span>
                </div>
              }
              updatingContent={
                <TextInputField
                  testId={'customFieldDetailNameInput'}
                  fieldLabel={translate('Field Name')}
                  value={fields.name}
                  baseValue={customField.name}
                  errors={fieldsErrors.name}
                  design={TextInputFieldDesigns.New}
                  placeholder={translate('Enter a name')}
                  onChange={(value): void => onFieldChange('name', value)}
                  disabled={patchFieldLoading}
                />
              }
              saveLoading={patchFieldLoading}
              onSave={(): boolean | Promise<boolean> => {
                if (!setErrorsAndReturnDidPassValidation()) return false
                return patchFieldSucceeded({
                  id: customFieldId,
                  body: { name: fields.name },
                })
              }}
              onCancel={(): void => {
                onFieldChange('name', customField.name)
                setFieldsErrors({ name: undefined })
              }}
              disallowUpdate={disableEditing}
              additionalContent={
                <div className={'flex-column gap-8'}>
                  <OnOffSwitchWithLabel
                    label={translate('Active')}
                    subLabel={translate(
                      'When active, everyone in the org can see and fill out this field. When inactive, the field will be hidden from view.',
                    )}
                    defaultOnOffState={customField.active}
                    testId={'customFieldDetailActiveSwitch'}
                    onChange={(value): Promise<boolean> =>
                      patchFieldSucceeded({
                        id: customFieldId,
                        body: { active: value },
                      })
                    }
                    disabled={disableEditing}
                  />
                  <OnOffSwitchWithLabel
                    label={translate('Required')}
                    subLabel={translate(
                      'If required, this field must be completed on transactions.',
                    )}
                    defaultOnOffState={customField.required}
                    testId={'customFieldDetailRequiredSwitch'}
                    onChange={(value): Promise<boolean> =>
                      patchFieldSucceeded({
                        id: customFieldId,
                        body: { required: value },
                      })
                    }
                    disabled={disableEditing}
                  />
                </div>
              }
            />
          }
          listComponent={
            <div className={'flex-column gap-3'}>
              <span className={'font-h3'}>
                {translate('Restrict to merchant categories')}
              </span>
              <span className={'font-md text-secondary'}>
                {translate(
                  'This field appears on all transactions when no merchant categories are selected.',
                )}
              </span>
              <Box
                noPadding
                noShadow
                outlineBorder
                css={mccGroupsBoxCss}
                data-testid={'customFieldDetailMccGroupsList'}
              >
                <DataGrid
                  autoHeight
                  rowHeight={53}
                  headerStyle={DataGridHeaderStyle.NoBackground}
                  rows={mccGroupRows}
                  columns={mccGroupColumns}
                  filtersBody={{}}
                  disableColumnFilter
                  disableColumnMenu
                  hideFooter
                  rowSelection={false}
                  onCellClick={(params): void => {
                    if (disableEditing || patchMccGroupsPending) return
                    // Skip the checkbox column — its Checkbox handles clicks
                    // itself via onChange.
                    if (params.field === MccGroupColumnField.Selected) return
                    const { id: groupId } = params.row as MccRow
                    void onToggleMccGroup(
                      groupId,
                      !selectedMccGroupIds.has(groupId),
                    )
                  }}
                  sx={{
                    '.MuiDataGrid-cell': {
                      padding: `${spacing(2)} ${spacing(4)}`,
                    },
                    '.MuiDataGrid-columnHeader': {
                      padding: `0 ${spacing(4)}`,
                    },
                    '.MuiDataGrid-row': {
                      cursor:
                        disableEditing || patchMccGroupsPending
                          ? 'default'
                          : 'pointer',
                    },
                    // Block all interaction during a patch so a second click
                    // can't race in. Applied via sx (not the Checkbox's
                    // `disabled` prop) so we don't invalidate the memoized
                    // columns on every patch.
                    ...(patchMccGroupsPending && {
                      '.MuiDataGrid-cell, .MuiDataGrid-row': {
                        pointerEvents: 'none',
                      },
                    }),
                  }}
                />
              </Box>
            </div>
          }
        />
      </LayoutPage>
    </>
  )
}

export default CustomFieldDetail
